import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import type { MinerSpec } from '../utils/minerTypes';
import { toast } from 'react-hot-toast';
import { updateMinersCache, miners as fallbackMiners } from '../utils/miners';
import { fetchMinerstatHardware, convertMinerstatToMinerSpec } from '../utils/api/minerstat';

// Cache for miners data
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let minersCache: MinerSpec[] | null = null;

export function useMiners() {
  const [miners, setMiners] = useState<MinerSpec[]>(minersCache || fallbackMiners);
  const [isLoading, setIsLoading] = useState(!minersCache);
  const [error, setError] = useState<string | null>(null);

  const fetchMiners = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if we can use cached data
      if (minersCache && Date.now() - lastFetchTime < CACHE_TTL) {
        setMiners(minersCache);
        setIsLoading(false);
        return;
      }

      // Fetch miners from Supabase
      const { data, error: supabaseError } = await supabase
        .from('miners')
        .select('*')
        .order('release_year', { ascending: false });

      if (supabaseError) throw supabaseError;

      if (data && data.length > 0) {
        // Map Supabase snake_case to camelCase
        const formattedMiners = data.map(miner => ({
          ...miner,
          hashrateUnit: miner.hashrate_unit,
          releaseYear: miner.release_year,
          supportedCoins: miner.specs?.supportedCoins || []
        })) as MinerSpec[];
        
        setMiners(formattedMiners);
        minersCache = formattedMiners;
        updateMinersCache(formattedMiners);
        lastFetchTime = Date.now();
      } else {
        // If no data, fetch from Minerstat and import
        console.log('No miners found, importing from Minerstat...');
        const minerstatData = await fetchMinerstatHardware();
        
        if (minerstatData.length > 0) {
          // Convert Minerstat data to our format
          const formattedData = minerstatData
            .filter(miner => miner.type === 'asic')
            .map(miner => convertMinerstatToMinerSpec(miner))
            .filter((miner): miner is NonNullable<typeof miner> => miner !== null);

          // Import data using the database function
          const { data: importResult, error: importError } = await supabase
            .rpc('import_minerstat_data', {
              minerstat_data: formattedData
            });

          if (importError) throw importError;

          // Fetch the imported data
          const { data: refreshedData, error: refreshError } = await supabase
            .from('miners')
            .select('*')
            .order('release_year', { ascending: false });

          if (refreshError) throw refreshError;
          
          if (refreshedData) {
            const formattedMiners = refreshedData.map(miner => ({
              ...miner,
              hashrateUnit: miner.hashrate_unit,
              releaseYear: miner.release_year,
              supportedCoins: miner.specs?.supportedCoins || []
            })) as MinerSpec[];
            
            setMiners(formattedMiners);
            minersCache = formattedMiners;
            updateMinersCache(formattedMiners);
            lastFetchTime = Date.now();

            // Show import results
            if (importResult) {
              toast.success(
                `Import completed:\n` +
                `- Inserted: ${importResult.inserted}\n` +
                `- Updated: ${importResult.updated}\n` +
                `- Skipped: ${importResult.skipped}`
              );
            }
          }
        } else {
          // If no data from Minerstat, use fallback data
          console.log('No data from Minerstat, using fallback data');
          setMiners(fallbackMiners);
          minersCache = fallbackMiners;
          updateMinersCache(fallbackMiners);
          lastFetchTime = Date.now();
        }
      }
    } catch (err: any) {
      console.error('Error fetching miners:', err);
      // Use cached data if available, otherwise fallback
      const fallbackData = minersCache || fallbackMiners;
      setMiners(fallbackData);
      setError('Failed to fetch miners - using cached data');
      toast.error('Failed to fetch miners - using cached data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMiners();
  }, []);

  const refresh = async () => {
    try {
      lastFetchTime = 0; // Force refresh
      minersCache = null; // Clear cache
      await fetchMiners();
      toast.success('Miners data refreshed');
    } catch (error) {
      toast.error('Failed to refresh miners data');
    }
  };

  return { miners, isLoading, error, refresh };
}